import * as React from 'react';
import { Switch, Route } from 'react-router-dom';
import { Lazy } from 'framework/components/ui/Lazy';
import { Page } from 'framework/components/ui/Page';
import { NoPermissionPage, AuthorizedRoute } from 'framework/authorization';
import { useAppRoutes } from 'framework/modules/routes';
import { view } from '@risingstack/react-easy-state';
import { LoadingSpinner } from 'framework/modules/contentmanagement/SharedContent/Helpers-Ui';

// React.lazy must be called outside render: a fresh wrapper each render is
// treated as a different component type and remounts the entire page subtree.
const lazyComponentCache = new WeakMap<() => Promise<any>, React.LazyExoticComponent<any>>();
const getLazyComponent = (importFn: () => Promise<any>) => {
  if (!lazyComponentCache.has(importFn)) {
    lazyComponentCache.set(importFn, React.lazy(importFn));
  }
  return lazyComponentCache.get(importFn)!;
};

export const PageRoutingRenderer = view(() => {
  const { localRoutes, isLoading } = useAppRoutes();

  return isLoading ? <LoadingSpinner visible={isLoading} /> : (
    <Switch>
      <Route path="/nopermission" exact component={() => <NoPermissionPage isFeaturesLoading={isLoading} />} />
      {/* page routes */}
      {localRoutes?.map((appRoute) => {
        return (
          <Route
            key={appRoute.path}
            path={appRoute.path}
            exact
            render={(routeProps) => {
              // Cached React.lazy keeps a stable component type, so React reuses the
              // instance across parent re-renders. Key location.key or pathname+search
              // remounts when the URL changes without recreating React.lazy on parent re-renders.
              return (
                <AuthorizedRoute routeClaim={appRoute.authClaim}>
                  <Lazy
                    key={routeProps.location.key || `${routeProps.location.pathname}${routeProps.location.search}`}
                    Component={getLazyComponent(appRoute.Component)}
                    pageInfo={appRoute}
                    appRoute={appRoute}
                    {...routeProps}
                  />
                </AuthorizedRoute>
              );
            }}
          />
        );
      })}
      {/* TODO: create proper Not found page */}
      <Route
        render={(routeProps) => (
          <Page
            hideBreadCrumbs
            pageInfo={{ pageTitle: 'Not found' }}
            pageParts={{ content: () => <div className="mrg-top-20">{routeProps.location.pathname}</div> }}
          />
        )}
      />
    </Switch>
  );
});
